import React, { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
    generateExternalLoginURL,
} from "../../config/externalLogin";
import { generateReturnCode } from "../../api/api";
import config from "../../config/config";
import FMBlogo from "../../assests/logo.png";

const FreeTrialSignup = () => {
    const [tosOpen, setTosOpen] = useState(false);

    const handleStartFreeTrial = async () => {
        try {
            const { return_code } = await generateReturnCode();
            sessionStorage.setItem("ivs_return_code", return_code);
            // Redirect URI points to backend - it validates Referer then redirects to frontend
            const redirectURI = `${config.API_BASE_URL}/ivs-callback/free-trial`;
            const loginURL = generateExternalLoginURL("EMAIL", redirectURI, return_code);
            window.location.href = loginURL;
        } catch (err) {
            console.error("Failed to generate return code:", err);
            alert("Could not start signup. Please try again.");
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "100vh",
                px: { xs: 2, sm: 3 },
                py: { xs: 3, sm: 4 },
                backgroundColor: "#f5f5f5",
            }}
        >
            <Card
                sx={{
                    maxWidth: 500,
                    width: "100%",
                    boxShadow: 3,
                    borderRadius: 2,
                }}
            >
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            mb: 3,
                        }}
                    >
                        <img
                            src={FMBlogo}
                            alt="Logo"
                            style={{
                                width: 200,
                                maxWidth: "100%",
                                height: "auto",
                                marginBottom: 24,
                            }}
                        />
                        <Typography
                            variant="h4"
                            component="h1"
                            gutterBottom
                            sx={{
                                fontWeight: 600,
                                textAlign: "center",
                                fontSize: { xs: "1.75rem", sm: "2rem" },
                            }}
                        >
                            Free Trial Signup
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            textAlign="center"
                            sx={{ mt: 1, mb: 2 }}
                        >
                            Start your free trial and simplify your queues!
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 1, rowGap: 0.3, mb: 3, pl: 2, width: "100%" }}>
                            {[
                                "30-day free trial",
                                "No credit card required",
                                "Unlimited branches",
                                "Unlimited customers/day",
                                "QR code queue",
                                "Real-time display",
                                "Advanced analytics",
                                "CRM integration",
                                "Priority support",
                                "Custom branding",
                            ].map((text) => (
                                <Typography key={text} variant="caption" sx={{ color: "#6b7280", display: "flex", alignItems: "center", gap: 0.5, textAlign: "left" }}>
                                    <Box component="span" sx={{ color: "#22c55e", fontWeight: 700, fontSize: "0.85rem" }}>&#10003;</Box>
                                    {text}
                                </Typography>
                            ))}
                        </Box>
                    </Box>

                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        onClick={handleStartFreeTrial}
                        sx={{
                            py: 1.5,
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                            textTransform: "none",
                            fontWeight: 600,
                        }}
                    >
                        Start Signup
                    </Button>

                    <Box sx={{ mt: 3, textAlign: "center" }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                            By continuing, you agree to our{" "}
                            <Box
                                component="span"
                                onClick={() => setTosOpen(true)}
                                sx={{
                                    color: "#2563eb",
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                    "&:hover": { color: "#1d4ed8" },
                                }}
                            >
                                terms of service
                            </Box>.
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            <Box mt={4} textAlign="center">
                <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                    <a href="#" style={{ textDecoration: "none", color: "inherit" }}>
                        easyQue
                    </a>{" "}
                    (c) by Digifront
                </Typography>
                <Typography variant="body2" mt={1} sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                    <a
                        href="mailto:sales@digifront.biz"
                        style={{ textDecoration: "none", color: "inherit" }}
                    >
                        sales@digifront.biz
                    </a>
                </Typography>
            </Box>

            {/* Terms of Service Modal */}
            <Dialog
                open={tosOpen}
                onClose={() => setTosOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
                    Terms of Service
                    <IconButton
                        onClick={() => setTosOpen(false)}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        1. Free Trial
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: "#4b5563" }}>
                        The easyQue free trial provides 30 days of full access to our queue management platform at no cost. No credit card or payment information is required to start. At the end of the trial period, your account will be paused until you choose to subscribe.
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        2. Account & Data
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: "#4b5563" }}>
                        You are responsible for maintaining the confidentiality of your account credentials. Any data you enter (branch details, services, staff information) remains your property. We will not share your data.
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        3. Acceptable Use
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: "#4b5563" }}>
                        You agree to use easyQue only for lawful queue management purposes. You may not attempt to disrupt the service, reverse-engineer the platform, or use it to send unsolicited communications to end-users.
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        4. Service Availability
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: "#4b5563" }}>
                        We strive to keep easyQue available at all times but do not guarantee uninterrupted service. Scheduled maintenance or unforeseen issues may cause temporary downtime. We will make all efforts to notify you in advance of planned maintenance.
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        5. Limitation of Liability
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: "#4b5563" }}>
                        easyQue is provided "as is" during the free trial. Digifront shall not be liable for any indirect, incidental, or consequential damages arising from the use of the service.
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        6. Changes to Terms
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#4b5563" }}>
                        We reserve the right to update these terms at any time. Continued use of the service after changes constitutes acceptance of the updated terms. For questions, contact us at sales@digifront.biz.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => setTosOpen(false)}
                        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                    >
                        I Understand
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FreeTrialSignup;
